import { useState } from "react";
import { Link } from "react-router";
import { ChevronRight, Lightbulb, Loader2, Plus, Sparkles } from "lucide-react";
import { PageHeader } from "@/components/AppShell";
import { Sheet } from "@/components/Sheet";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  NumberStepper,
  PageLoader,
  SectionTitle,
  Segmented,
  TextField,
  Textarea,
  Toggle,
} from "@/components/ui";
import { IconPicker, KidChips, Points } from "@/components/bits";
import { api } from "@/lib/api";
import { useAction, useParentJobs, useRewardTypes } from "@/lib/queries";
import { useSession } from "@/lib/session";
import { REPEAT_LABELS } from "@/lib/format";
import { quickPrice } from "@shared/pricing";
import { JOB_TEMPLATES, type JobTemplate } from "@shared/templates";
import type { JobDTO, JobPriceDTO, JobRepeat } from "@shared/types";
import { AdvisorSource, ConfirmDelete, RewardEquivalents, useAdvice } from "./Rewards";

type Draft = Pick<
  JobDTO,
  "title" | "description" | "icon" | "reward" | "repeat" | "maxPerPeriod" | "kidIds" | "workItOff" | "estimatedMinutes"
> & { id?: string };

const empty: Draft = {
  title: "",
  description: null,
  icon: "🧰",
  reward: 20,
  repeat: "anytime",
  maxPerPeriod: null,
  kidIds: [],
  workItOff: false,
  estimatedMinutes: null,
};

const REPEAT_HINTS: Record<JobRepeat, string> = {
  once: "One kid does it once, then it's gone.",
  daily: "Opens up again every day.",
  weekly: "Opens up again at the start of each week.",
  anytime: "Always open — kids can do it as often as they like.",
};

const isRepeating = (repeat: JobRepeat) => repeat === "daily" || repeat === "weekly";

function toDraft(j: JobDTO): Draft {
  return {
    id: j.id,
    title: j.title,
    description: j.description,
    icon: j.icon,
    reward: j.reward,
    repeat: j.repeat,
    maxPerPeriod: j.maxPerPeriod,
    kidIds: j.kidIds,
    workItOff: j.workItOff,
    estimatedMinutes: j.estimatedMinutes,
  };
}

function toBody(d: Draft) {
  return {
    title: d.title.trim(),
    description: d.description?.trim() || null,
    icon: d.icon,
    reward: Math.round(d.reward),
    repeat: d.repeat,
    maxPerPeriod: isRepeating(d.repeat) ? (d.maxPerPeriod ?? 1) : null,
    kidIds: d.kidIds,
    workItOff: d.workItOff,
    estimatedMinutes: d.estimatedMinutes,
  };
}

function describeRepeat(j: Pick<JobDTO, "repeat" | "maxPerPeriod">) {
  const base = REPEAT_LABELS[j.repeat] ?? j.repeat;
  const n = j.maxPerPeriod ?? 1;
  return isRepeating(j.repeat) && n > 1 ? `${base} · up to ${n}×` : base;
}

function JobForm({ draft, setDraft }: { draft: Draft; setDraft: (d: Draft) => void }) {
  const { kids, family } = useSession();
  const [suggestion, setSuggestion] = useState<JobPriceDTO | null>(null);
  const set = (patch: Partial<Draft>) => setDraft({ ...draft, ...patch });
  const price = useAdvice(
    (d: Draft) =>
      api.post<JobPriceDTO>("/advisor/price-job", {
        title: d.title.trim(),
        description: d.description?.trim() || null,
        estimatedMinutes: d.estimatedMinutes,
        kidId: d.kidIds.length === 1 ? d.kidIds[0] : undefined,
      }),
    setSuggestion,
  );
  const canSuggest = !!draft.title.trim();

  return (
    <div className="space-y-5">
      <TextField
        label="Job"
        placeholder="Wash the car"
        value={draft.title}
        maxLength={80}
        onChange={(e) => set({ title: e.target.value })}
      />
      <Field label="Icon">
        <IconPicker value={draft.icon} onChange={(icon) => set({ icon })} />
      </Field>

      <Field label={draft.workItOff ? "Pays back up to" : "Reward"}>
        <NumberStepper value={draft.reward} onChange={(reward) => set({ reward })} min={1} max={100_000} step={5} suffix={family.currencyIcon} />
        {!draft.workItOff && <RewardEquivalents points={draft.reward} className="mt-2" />}
        <div className="pt-1">
          <button
            type="button"
            onClick={() => price.mutate(draft)}
            disabled={!canSuggest || price.isPending}
            className="-ml-1 inline-flex h-10 items-center gap-1.5 rounded-xl px-2 text-sm font-bold text-violet transition hover:bg-violet-soft disabled:opacity-60"
          >
            {price.isPending ? <Loader2 className="size-4 animate-spin" /> : <Sparkles className="size-4" />}
            {price.isPending ? "Thinking…" : canSuggest ? "Suggest a fair price" : "Add a title to get a price suggestion"}
          </button>
        </div>
        {suggestion && (
          <div className="rounded-2xl bg-violet-soft p-3">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-xs font-bold text-violet">Fair range</div>
                <div className="flex items-center gap-1 text-lg">
                  <Points value={suggestion.low} family={family} /> <span className="text-muted">–</span>
                  <Points value={suggestion.high} family={family} />
                </div>
              </div>
              <Button
                size="sm"
                onClick={() =>
                  set({ reward: suggestion.suggestedReward, estimatedMinutes: draft.estimatedMinutes ?? suggestion.estimatedMinutes })
                }
                disabled={draft.reward === suggestion.suggestedReward}
              >
                Use {family.currencyIcon} {suggestion.suggestedReward.toLocaleString()}
              </Button>
            </div>
            <p className="mt-2 text-sm text-muted">{suggestion.rationale}</p>
            <div className="mt-2">
              <AdvisorSource source={suggestion.source} />
            </div>
          </div>
        )}
      </Field>

      <Field label="Repeats" hint={REPEAT_HINTS[draft.repeat]}>
        <Segmented
          value={draft.repeat}
          onChange={(repeat) => set({ repeat, maxPerPeriod: isRepeating(repeat) ? (draft.maxPerPeriod ?? 1) : null })}
          options={[
            { value: "anytime", label: "Any time" },
            { value: "daily", label: "Daily" },
            { value: "weekly", label: "Weekly" },
            { value: "once", label: "Once" },
          ]}
        />
      </Field>

      {isRepeating(draft.repeat) && (
        <Field label={`Times per ${draft.repeat === "daily" ? "day" : "week"}`} hint="How often each kid can do it.">
          <NumberStepper value={draft.maxPerPeriod ?? 1} onChange={(maxPerPeriod) => set({ maxPerPeriod })} min={1} max={10} />
        </Field>
      )}

      <Field label="Who">
        <KidChips kids={kids} value={draft.kidIds} onChange={(kidIds) => set({ kidIds })} allLabel="Every kid" />
      </Field>

      <TextField
        label="About how long? (optional)"
        type="number"
        inputMode="numeric"
        min={1}
        max={1440}
        placeholder="Minutes"
        value={draft.estimatedMinutes ?? ""}
        onChange={(e) => {
          const n = Math.round(Number(e.target.value));
          set({ estimatedMinutes: e.target.value && n > 0 ? Math.min(n, 1440) : null });
        }}
        hint="Helps the advisor suggest a fair price."
      />

      <Field label="Details (optional)">
        <Textarea
          placeholder="What does “done” look like?"
          value={draft.description ?? ""}
          maxLength={500}
          onChange={(e) => set({ description: e.target.value || null })}
        />
      </Field>

      <div className="space-y-2 rounded-3xl bg-sunken/60 p-4 ring-1 ring-line">
        <Toggle
          checked={draft.workItOff}
          onChange={(workItOff) => set({ workItOff })}
          label="Work-it-off job"
          description="Only shown to kids below zero. It pays back what they owe — up to zero, never extra."
        />
        {draft.workItOff && (!family.workItOffEnabled || !family.allowNegative) && (
          <p className="rounded-2xl bg-warn-soft px-3 py-2 text-sm font-semibold text-warn">
            {!family.workItOffEnabled ? "Work it off is turned off" : "Balances can't go below zero right now"}, so kids won't see
            this yet.{" "}
            <Link to="/parent/plan/discipline" className="underline">
              Discipline settings
            </Link>
          </p>
        )}
      </div>

      {draft.id && <p className="text-xs text-muted">Archiving takes it off kids' lists. Past payouts stay in history.</p>}
    </div>
  );
}

export function JobsEditor() {
  const { family, memberById } = useSession();
  const { data, isLoading } = useParentJobs();
  const { data: rewardData } = useRewardTypes();
  const [draft, setDraft] = useState<Draft | null>(null);

  const save = useAction(
    (d: Draft) => (d.id ? api.patch(`/jobs/${d.id}`, toBody(d)) : api.post("/jobs", toBody(d))),
    { success: "Saved", onSuccess: () => setDraft(null) },
  );
  const archive = useAction((id: string) => api.del(`/jobs/${id}`), { success: "Job archived", onSuccess: () => setDraft(null) });

  const pointsPerDollar =
    rewardData?.rewardTypes.find((t) => t.active && t.kind === "cash" && t.pointsPerUnit)?.pointsPerUnit ?? 10;
  const addTemplate = useAction(
    async (t: JobTemplate) => {
      let reward: number;
      try {
        reward = (await api.post<JobPriceDTO>("/advisor/price-job", { title: t.title, estimatedMinutes: t.minutes })).suggestedReward;
      } catch {
        // Advisor unreachable: fall back to the same rule of thumb the built-in advisor uses.
        reward = quickPrice({ pointsPerDollar, currencyName: family.currencyName }, { title: t.title, estimatedMinutes: t.minutes })
          .suggestedReward;
      }
      return api.post<JobDTO>("/jobs", {
        title: t.title,
        icon: t.icon,
        reward,
        repeat: t.repeat,
        maxPerPeriod: isRepeating(t.repeat) ? 1 : null,
        estimatedMinutes: t.minutes,
      });
    },
    { success: (job) => `Added “${job.title}” for ${job.reward} ${family.currencyName}` },
  );

  if (isLoading || !data) return <PageLoader />;

  const active = data.jobs.filter((j) => j.status === "active");
  const pitches = data.jobs.filter((j) => j.status === "pending");
  const paid = active.filter((j) => !j.workItOff);
  const recovery = active.filter((j) => j.workItOff);
  const existing = new Set(active.map((j) => j.title.toLowerCase()));
  const suggestions = JOB_TEMPLATES.filter((t) => !existing.has(t.title.toLowerCase())).slice(0, 6);

  const who = (j: JobDTO) =>
    j.kidIds.length
      ? j.kidIds
          .map((id) => memberById(id)?.name)
          .filter(Boolean)
          .join(", ")
      : "Every kid";

  const renderJob = (j: JobDTO) => {
    const pitcher = j.origin === "pitch" ? memberById(j.pitchedBy) : undefined;
    return (
      <Card key={j.id} className="cursor-pointer p-3 transition hover:ring-brand/40" onClick={() => setDraft(toDraft(j))}>
        <div className="flex items-center gap-3">
          <span className="flex size-12 shrink-0 items-center justify-center rounded-2xl bg-sunken text-2xl">{j.icon}</span>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="truncate font-extrabold">{j.title}</span>
              {j.workItOff && <Badge tone="violet">Work it off</Badge>}
              {pitcher && <Badge tone="coin">💡 {pitcher.name}'s idea</Badge>}
            </div>
            <div className="truncate text-sm text-muted">
              {describeRepeat(j)} · {who(j)}
              {j.estimatedMinutes ? ` · ~${j.estimatedMinutes} min` : ""}
            </div>
          </div>
          <Points value={j.reward} family={family} className="shrink-0 rounded-full bg-coin-soft px-2.5 py-1 text-sm text-coin-ink" />
        </div>
      </Card>
    );
  };

  return (
    <div>
      <PageHeader
        title="Jobs"
        subtitle="Optional, paid work — unlocked by responsibilities"
        back="/parent/plan"
        actions={
          <Button size="sm" onClick={() => setDraft({ ...empty })}>
            <Plus className="size-4" /> Add
          </Button>
        }
      />

      {pitches.length > 0 && (
        <Link
          to="/parent/approvals"
          className="mt-2 flex items-center gap-3 rounded-2xl bg-coin-soft px-4 py-3 text-coin-ink transition hover:brightness-95"
        >
          <Lightbulb className="size-5 shrink-0" />
          <span className="min-w-0 flex-1 text-sm font-bold">
            {pitches.length === 1 ? "1 pitch" : `${pitches.length} pitches`} waiting — your kids proposed their own jobs.
          </span>
          <ChevronRight className="size-5 shrink-0" />
        </Link>
      )}

      {active.length === 0 ? (
        <div className="mt-4">
          <EmptyState icon="🧰" title="No jobs yet" action={<Button onClick={() => setDraft({ ...empty })}>Add the first one</Button>}>
            Jobs are optional extra work kids do to earn {family.currencyName}. Try a quick add below.
          </EmptyState>
        </div>
      ) : (
        <>
          {paid.length > 0 && <div className="mt-4 space-y-2">{paid.map(renderJob)}</div>}
          {recovery.length > 0 && (
            <>
              <SectionTitle>Work-it-off jobs</SectionTitle>
              <p className="-mt-1 mb-2 px-1 text-sm text-muted">Only for kids below zero. They pay back up to zero.</p>
              <div className="space-y-2">{recovery.map(renderJob)}</div>
            </>
          )}
        </>
      )}

      {suggestions.length > 0 && (
        <div className="mt-8">
          <h2 className="mb-2 px-1 text-lg font-extrabold">Quick add</h2>
          <p className="-mt-1 mb-2 px-1 text-xs text-muted">Priced for you by the advisor — tap a job afterwards to adjust.</p>
          <div className="flex flex-wrap gap-2">
            {suggestions.map((t) => {
              const busy = addTemplate.isPending && addTemplate.variables?.title === t.title;
              return (
                <button
                  key={t.title}
                  type="button"
                  disabled={addTemplate.isPending}
                  onClick={() => addTemplate.mutate(t)}
                  className="inline-flex items-center gap-1.5 rounded-full bg-card px-3 py-2 text-sm font-bold shadow-card ring-1 ring-line transition hover:ring-brand disabled:opacity-60"
                >
                  <span>{t.icon}</span> {t.title}
                  {busy ? <Loader2 className="size-4 animate-spin text-brand" /> : <Plus className="size-4 text-brand" />}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <Sheet
        open={!!draft}
        onClose={() => setDraft(null)}
        title={draft?.id ? "Edit job" : "New job"}
        footer={
          <div className="flex gap-2">
            {draft?.id && (
              <ConfirmDelete
                label="Archive"
                confirmLabel="Archive?"
                onConfirm={() => draft.id && archive.mutate(draft.id)}
                loading={archive.isPending}
              />
            )}
            <Button
              block
              onClick={() => draft && save.mutate(draft)}
              loading={save.isPending}
              disabled={!draft?.title.trim() || !(draft.reward > 0)}
            >
              Save
            </Button>
          </div>
        }
      >
        {draft && <JobForm draft={draft} setDraft={setDraft} />}
      </Sheet>
    </div>
  );
}
